/**
 * Owner: Masa Bayu
 * File: commerce-pricing.ts
 * Date:Time: 2026-09-15 22:25
 * Description: T3-E1 deterministic pricing — fees/rewards contracts, no payment charge.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { createQxreadAttributionContract, createQxreadLedgerLineContract, evaluateAutoAffiliateReferralEligibility, QXREAD_COMMERCE_RATE_CONTRACT, } from './commerce-boundary';
import { assertValidId } from './guards';
function assertMinorUnits(value, label) {
    if (typeof value !== 'number' ||
        !Number.isFinite(value) ||
        !Number.isInteger(value) ||
        value < 0) {
        throw new Error(`${label} must be a non-negative finite integer`);
    }
    return value;
}
/** Deterministic basis-point allocation (floor). */
export function applyBasisPoints(amountMinorUnits, bps) {
    assertMinorUnits(amountMinorUnits, 'QXRead amountMinorUnits');
    if (!Number.isInteger(bps) || bps < 0) {
        throw new Error('QXRead bps must be a non-negative integer');
    }
    return Math.floor((amountMinorUnits * bps) / 10_000);
}
export function createCommerceOffer(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead commerce offer');
    assertValidId(options.publicationId, 'QXRead commerce offer publicationId');
    const title = options.title.trim();
    if (title.length === 0) {
        throw new Error('QXRead commerce offer title is required');
    }
    const currency = (options.currency ?? 'USD').trim().toUpperCase();
    if (currency.length === 0) {
        throw new Error('QXRead commerce offer currency is required');
    }
    return Object.freeze({
        publicationId: options.publicationId,
        qxpPublicationRef: options.qxpPublicationRef ?? null,
        title,
        sellerPriceMinorUnits: assertMinorUnits(options.sellerPriceMinorUnits, 'QXRead sellerPriceMinorUnits'),
        currency,
        saleableOnQxread: options.saleableOnQxread ?? true,
        sellerKind: options.sellerKind ?? 'author',
        marketplaceReady: options.marketplaceReady ?? true,
    });
}
export function createPriceSnapshot(offer, snappedAt = new Date().toISOString()) {
    return Object.freeze({
        publicationId: offer.publicationId,
        title: offer.title,
        qxpPublicationRef: offer.qxpPublicationRef,
        sellerPriceMinorUnits: offer.sellerPriceMinorUnits,
        currency: offer.currency,
        snappedAt,
    });
}
export function calculateCommerceTotals(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead commerce totals');
    assertValidId(input.correlationId, 'QXRead commerce totals correlationId');
    if (input.lines.length === 0) {
        throw new Error('QXRead commerce totals require at least one line');
    }
    const currency = input.lines[0]?.snapshot.currency;
    if (currency === undefined) {
        throw new Error('QXRead commerce totals currency missing');
    }
    let sellerSubtotalMinorUnits = 0;
    const uniqueIds = new Set();
    const attributions = [];
    for (const line of input.lines) {
        if (line.snapshot.currency !== currency) {
            throw new Error('QXRead commerce totals require a single currency');
        }
        if (!Number.isInteger(line.quantity) || line.quantity < 1) {
            throw new Error('QXRead commerce line quantity must be an integer >= 1');
        }
        sellerSubtotalMinorUnits += line.snapshot.sellerPriceMinorUnits * line.quantity;
        uniqueIds.add(line.publicationId);
        const eligibility = evaluateAutoAffiliateReferralEligibility({
            publicationId: line.publicationId,
            sellerKind: line.sellerKind ?? 'author',
            saleableOnQxread: true,
        });
        if (eligibility.affiliateEligible || eligibility.referralEligible) {
            attributions.push(createQxreadAttributionContract({
                publicationId: line.publicationId,
                affiliateAccountId: input.affiliateAccountId ?? null,
                referralAccountId: input.referralAccountId ?? null,
                state: 'pending_order',
            }));
        }
    }
    const serviceFeeMinorUnits = applyBasisPoints(sellerSubtotalMinorUnits, QXREAD_COMMERCE_RATE_CONTRACT.serviceFeeBuyerPaidBps);
    const affiliateContractMinorUnits = applyBasisPoints(sellerSubtotalMinorUnits, QXREAD_COMMERCE_RATE_CONTRACT.affiliateAutomaticBps);
    const referralContractMinorUnits = applyBasisPoints(sellerSubtotalMinorUnits, QXREAD_COMMERCE_RATE_CONTRACT.referralAutomaticBps);
    const rbsContractMinorUnits = uniqueIds.size * QXREAD_COMMERCE_RATE_CONTRACT.rbsUsdCentsPerUniqueEbookWithSaleInMonth;
    const buyerTotalMinorUnits = sellerSubtotalMinorUnits + serviceFeeMinorUnits;
    const ledgerLines = Object.freeze([
        createQxreadLedgerLineContract({
            concept: 'seller_base_price',
            amountMinorUnits: sellerSubtotalMinorUnits,
            currency,
            correlationId: input.correlationId,
        }),
        createQxreadLedgerLineContract({
            concept: 'service_fee',
            amountMinorUnits: serviceFeeMinorUnits,
            currency,
            correlationId: input.correlationId,
        }),
        createQxreadLedgerLineContract({
            concept: 'affiliate',
            amountMinorUnits: affiliateContractMinorUnits,
            currency,
            correlationId: input.correlationId,
        }),
        createQxreadLedgerLineContract({
            concept: 'referral',
            amountMinorUnits: referralContractMinorUnits,
            currency,
            correlationId: input.correlationId,
        }),
        createQxreadLedgerLineContract({
            concept: 'rbs',
            amountMinorUnits: rbsContractMinorUnits,
            currency,
            correlationId: input.correlationId,
        }),
    ]);
    return Object.freeze({
        currency,
        sellerSubtotalMinorUnits,
        serviceFeeMinorUnits,
        buyerTotalMinorUnits,
        affiliateContractMinorUnits,
        referralContractMinorUnits,
        rbsContractMinorUnits,
        uniquePublicationCount: uniqueIds.size,
        ledgerLines,
        attributions: Object.freeze(attributions),
    });
}
//# sourceMappingURL=commerce-pricing.js.map